import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { draftBoardService } from '../services/draftBoardService';

const DRAFT_KEYS = {
  all: ['drafts'] as const,
  detail: (id: string) => ['drafts', id] as const,
};

export const useDrafts = (params?: { limit?: number; offset?: number }) =>
  useQuery(DRAFT_KEYS.all, () => draftBoardService.list(params));

export const useDraftCount = (enabled = true) =>
  useQuery(['drafts', 'count'], () => draftBoardService.list({ limit: 1 }), {
    select: (data) => data.total,
    // Invalidation-only — ingest/prune mutations refresh this; the header badge
    // isn't worth polling for on every page.
    staleTime: Infinity,
    enabled,
  });

export const useDraft = (id: string) =>
  useQuery(DRAFT_KEYS.detail(id), () => draftBoardService.get(id), {
    enabled: !!id,
  });

/** Step 1 of 2: parse text → get preview items, nothing saved */
export const useParseText = () =>
  useMutation((text: string) => draftBoardService.parse(text));

/** Step 2 of 2: confirm pre-parsed items → save to board */
export const useIngestDrafts = () => {
  const qc = useQueryClient();
  return useMutation(
    (items: string[]) => draftBoardService.ingest(items),
    { onSuccess: () => qc.invalidateQueries(DRAFT_KEYS.all) }
  );
};

export const useBulkPrune = () => {
  const qc = useQueryClient();
  return useMutation(
    (ids: string[]) => draftBoardService.bulkPrune(ids),
    { onSuccess: () => qc.invalidateQueries(DRAFT_KEYS.all) }
  );
};
